// =============================================================================
// PendingFarmLoads — the packhouse side of the farm→packhouse wagon handshake.
//
// Renders inside Deliveries as a "From farms" inbox. Lists farm_packhouse_loads
// that linked FARM tenants have submitted to this packhouse and not yet had
// answered. Receive calls receive_farm_load (mig 20260912000000), which
// atomically creates the deliveries row and flips the load — so a received
// wagon becomes an ordinary intake and feeds every downstream rollup. Reject
// records a reason the farm sees back in their Loads register.
//
// ⚠️ Rendered on DATA PRESENCE, not on a flow flag (FC-39). A packhouse that
// has never been sent a load sees nothing; one that has, sees it regardless of
// its production_flow. Gating this on VEG would hide a real pending load from
// a citrus packhouse whose grower happens to run the farm app.
//
// UF-10 (F01): Receive / Reject render only for the tier both RPCs admit (the
// active-tenant role MANAGER / ADMIN / SUPER_ADMIN, or platform mode) — anyone
// else used to count and type the number and then be refused. The questions are
// asked BEFORE mutate(), and a received wagon leaves a "Received … · Open
// delivery" line built from the delivery id the RPC returns, instead of the
// card simply vanishing.
// =============================================================================
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Inbox, Check, X as XIcon } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { usePendingFarmLoads, type PendingFarmLoad } from '@/hooks/usePendingFarmLoads'
import { promptDialog, alertDialog } from '@/components/Dialogs'
import { withTimeout } from '@/lib/saveTimeout'
import { fmtDateZA } from '@/lib/dates'
import { friendlyError, throwFriendly } from '@/lib/friendlyError'
import { useCurrentUser } from '@/lib/useCurrentUser'
import { usePlatformView, useSupportView } from '@/lib/platformView'
import { canAnswerFarmLoad, canReceiveFarmLoad } from '@/lib/roles'
import { unitNounFor } from '@/lib/unitNoun'
import WidgetRetry from '@/components/WidgetRetry'
import { tenantRoleOf } from '@/modules/farm/blockEditGate'

type PendingLoad = PendingFarmLoad

/** The two RPCs' own RAISEs carry the load's UUID ("Load 4c1e-… is already
 *  RECEIVED"), and 23514 / 42501 would otherwise collapse to a generic
 *  sentence. Mapped against mig 20261001084517 (receive l.83-104, reject
 *  l.236-256). */
export const FARM_LOAD_DOMAIN: [RegExp, string][] = [
  [/is already (RECEIVED|REJECTED)/i,
    'Someone has already answered this load. The list has been refreshed.'],
  [/not found/i,
    'This load is no longer on file — the farm may have withdrawn it. The list has been refreshed.'],
  [/was not sent to your packhouse/i,
    'This load was sent to a different business. Switch to the business it was sent to.'],
  [/Only managers \/ admins of this packhouse/i,
    'A manager or admin of this packhouse receives or rejects farm loads.'],
  [/Received count must be greater than zero/i,
    'Enter how many arrived — at least one.'],
  [/A reason is required/i,
    'Say why the load is rejected — the farm sees the reason.'],
]

/** "14 wagons", "1 bin" — the app's one pluraliser (CLAUDE.md), not `+ 's'`. */
function plural(n: number, label: string): string {
  const noun = unitNounFor([label])
  return `${n} ${n === 1 ? noun.singular : noun.plural}`
}

/** What a receive left behind: the line that replaces the vanished card. */
interface Received { loadId: string; count: number; label: string; growerId: string; deliveryId: string | null }

// Cyclomatic 7, all of it conditional JSX — four optional facts on one card
// (vehicle, crates, est-kg, the farm's note). CRAP = CC^2 * (1-coverage)^3 + CC,
// so an uncovered markup component trips the gate at low real complexity.
// fallow-ignore-next-line complexity
function LoadInboxCard({ l, growerName, busy, canReceive, canReject, onReceive, onReject }: {
  l: PendingLoad
  growerName: string | null
  busy: boolean
  /** undefined while the profile is still loading: show neither the buttons
   *  nor the refusal line, so a manager never reads "A manager receives…". */
  canReceive: boolean | undefined
  /** Reject is manager-only; a scale operator receives but cannot reject. */
  canReject: boolean | undefined
  onReceive: (l: PendingLoad) => void
  onReject:  (l: PendingLoad) => void
}) {
  return (
    <div className="card p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold">
            {plural(l.container_count, l.container_label)}
            {growerName && <span className="text-fg-muted"> · {growerName}</span>}
          </p>
          <p className="text-[11px] text-fg-muted">
            Sent {fmtDateZA(l.submitted_at.slice(0, 10))}
            {l.variety && <span className="text-lime-300/90"> · farm says: {l.variety}</span>}
            {l.vehicle_ref && ` · ${l.vehicle_ref}`}
            {l.crate_count != null && ` · ${l.crate_count} crates`}
            {l.estimated_kg != null && ` · ~${l.estimated_kg} kg`}
          </p>
        </div>
      </div>
      {/* The one free-text field that crosses the tenant edge. Shown prominently
          because it is the farm telling the packhouse something about THIS load. */}
      {l.notes_for_packhouse && (
        <p className="text-[11px] text-amber-200 bg-amber-900/20 rounded px-2 py-1">
          {l.notes_for_packhouse}
        </p>
      )}
      {canReceive === false && (
        <p className="text-[11px] text-fg-muted">A manager receives farm loads</p>
      )}
      {canReceive && <div className="flex gap-2">
        <button type="button" disabled={busy} onClick={() => onReceive(l)}
          className="flex-1 min-h-[44px] rounded-lg bg-emerald-700 hover:bg-emerald-600 text-white text-sm font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
          <Check size={14} /> Receive
        </button>
        {canReject && <button type="button" disabled={busy} onClick={() => onReject(l)}
          className="min-h-[44px] px-3 rounded-lg surface hover:bg-raised text-fg-secondary text-sm inline-flex items-center gap-1.5 disabled:opacity-50">
          <XIcon size={14} /> Reject
        </button>}
      </div>}
    </div>
  )
}

// Two queries, a mutation and the honest-error branch. The decisions that
// matter — what a receive/reject actually does — are in the two functions
// below and in the RPCs, which the pgTAP suite covers end to end.
// fallow-ignore-next-line complexity
export default function PendingFarmLoads({ tenantId }: { tenantId: string | null }) {
  const qc = useQueryClient()
  const [busyId, setBusyId] = useState<string | null>(null)

  const loadsQ = usePendingFarmLoads(tenantId)

  const growersQ = useQuery({
    queryKey: ['pending-farm-load-growers', tenantId],
    enabled:  !!tenantId && (loadsQ.data?.length ?? 0) > 0,
    queryFn: async () => {
      const { data, error } = await supabase.from('growers').select('grower_id, name')
      if (error) throw error
      return new Map((data ?? []).map(g => [g.grower_id as string, g.name as string]))
    },
  })

  const meQ = useCurrentUser()
  const me = meQ.data
  // A FAILED profile read leaves `me` undefined for good, so canReceive stays
  // undefined and the card shows neither the buttons nor the refusal line —
  // silently. Loading stays quiet; only a failed read says so, with a retry.
  const profileFailed = me === undefined && meQ.isError
  const platformMode = usePlatformView()
  const supportView  = useSupportView()
  // Mirrors the RPCs' gates exactly: platform mode OR an active-tenant role of
  // MANAGER / ADMIN / SUPER_ADMIN (reject), plus SCALE_OPERATOR (receive, UF-10). Not a permission key — a custom role holding
  // deliveries:manage would see Receive and still be refused. A support view
  // is read-only by ruling and mints no tenant role, so the RPC refuses it.
  const canReceive: boolean | undefined = me === undefined
    ? undefined
    : !supportView && (platformMode || canReceiveFarmLoad(tenantRoleOf(me)))
  const canReject: boolean | undefined = me === undefined
    ? undefined
    : !supportView && (platformMode || canAnswerFarmLoad(tenantRoleOf(me)))

  const [received, setReceived] = useState<Received[]>([])

  type Act =
    | { kind: 'receive'; l: PendingLoad; count: number }
    | { kind: 'reject';  l: PendingLoad; reason: string }

  // The questions are asked BEFORE mutate() (CLAUDE.md): the save watchdog
  // starts at mutate(), and a declined prompt is not a save at all.
  const act = useMutation({
    mutationFn: async (a: Act): Promise<string | null> => {
      if (a.kind === 'reject') {
        const { error } = await withTimeout(
          supabase.rpc('reject_farm_load', { p_load_id: a.l.load_id, p_reason: a.reason }),
          'PendingFarmLoads: reject_farm_load')
        if (error) throwFriendly(error, FARM_LOAD_DOMAIN)
        return null
      }
      const { data, error } = await withTimeout(
        supabase.rpc('receive_farm_load', { p_load_id: a.l.load_id, p_received_count: a.count, p_delivery_id: null }),
        'PendingFarmLoads: receive_farm_load')
      if (error) throwFriendly(error, FARM_LOAD_DOMAIN)
      return typeof data === 'string' ? data : null
    },
    onSettled: () => {
      setBusyId(null)
      // Also after a failure: "already answered" means the list is stale.
      void qc.invalidateQueries({ queryKey: ['pending-farm-loads', tenantId] })
    },
    onSuccess: (deliveryId, a) => {
      void qc.invalidateQueries({ queryKey: ['deliveries'] })
      if (a.kind === 'receive') {
        setReceived(r => [{ loadId: a.l.load_id, count: a.count, label: a.l.container_label,
          growerId: a.l.grower_id, deliveryId },
          ...r.filter(x => x.loadId !== a.l.load_id)])
      }
    },
    onError: async (e: Error) => {
      await alertDialog({ title: 'Could not do that', message: friendlyError(e, FARM_LOAD_DOMAIN).message })
    },
  })

  async function startReject(l: PendingLoad) {
    const reason = await promptDialog({
      title:   'Reject this load?',
      message: 'The farm sees this reason on their Loads register. Say what was wrong so they can act on it.',
      confirm: 'Reject load',
    })
    if (!reason || !reason.trim()) return
    setBusyId(l.load_id)
    act.mutate({ kind: 'reject', l, reason: reason.trim() })
  }

  // The count semantics are pinned by assertion 9 of the pgTAP suite.
  async function startReceive(l: PendingLoad) {
    // Defaulting to what the farm sent is deliberate: a receiver who does not
    // re-count has agreed, and that IS the record.
    const typed = await promptDialog({
      title:   `Receive ${l.container_count} ${l.container_label.toLowerCase()}(s)?`,
      message: `How many actually arrived? Leave it as ${l.container_count} if that is right — if you count something different, BOTH figures are kept and the farm sees the difference.`,
      confirm: 'Receive',
      defaultValue: String(l.container_count),
      // Numeric keypad: this is answered at a weighbridge, on a phone.
      inputType: 'number',
      inputMode: 'numeric',
    })
    if (typed === null) return
    const n = Number(typed)
    if (!Number.isInteger(n) || n < 1) {
      await alertDialog({ title: 'Could not do that', message: 'Enter how many arrived — a whole number, at least one.' })
      return
    }
    setBusyId(l.load_id)
    act.mutate({ kind: 'receive', l, count: n })
  }

  // The hand-off a receive leaves: survives the card vanishing (and the inbox
  // emptying), names what came in, and opens the delivery it became.
  const receivedLines = received.length > 0 && (
    <div className="space-y-2">
      {received.map(r => {
        // Looked up at render: the growers read may land after the tap.
        const grower = growersQ.data?.get(r.growerId)
        return (
        <div key={r.loadId} role="status"
          className="card p-2 pl-3 flex items-center gap-2 text-sm text-emerald-200 bg-emerald-900/20">
          <Check size={14} className="shrink-0" />
          <span className="min-w-0 flex-1">Received {plural(r.count, r.label)}{grower && ` · ${grower}`}</span>
          {r.deliveryId && (
            <Link to={`/deliveries/${r.deliveryId}`}
              className="min-h-[44px] px-3 inline-flex items-center rounded-lg text-emerald-300 hover:text-emerald-200 underline font-semibold shrink-0">
              Open delivery
            </Link>
          )}
          <button type="button" aria-label="Dismiss"
            onClick={() => setReceived(x => x.filter(y => y.loadId !== r.loadId))}
            className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-lg text-fg-muted hover:text-fg-secondary shrink-0">
            <XIcon size={16} />
          </button>
        </div>
        )
      })}
    </div>
  )

  // ⚠️ A FAILED read must never render as "no loads waiting" — that is a false
  // all-clear on fruit standing in the yard. Say the fault instead.
  if (loadsQ.isError) {
    return (
      <section className="p-4 pb-0 space-y-2">
        {receivedLines}
        <p className="card p-3 text-xs text-red-300">
          Couldn't load the farm inbox. Any wagons waiting are still waiting — retry, don't assume none.
        </p>
      </section>
    )
  }

  const loads = loadsQ.data ?? []
  if (loads.length === 0) {
    // presence-gated: nothing sent, nothing shown — except what was just received
    return receivedLines ? <section className="p-4 pb-0">{receivedLines}</section> : null
  }

  return (
    <section className="p-4 pb-0 space-y-2">
      <h2 className="text-sm font-semibold text-fg-secondary flex items-center gap-2">
        <Inbox size={16} /> From farms
        <span className="text-[10px] rounded px-1.5 py-0.5 surface text-fg-muted">{loads.length}</span>
      </h2>
      {receivedLines}
      {profileFailed && (
        <div className="card p-3 flex flex-wrap items-center gap-2 text-xs text-amber-200">
          <span className="min-w-0 flex-1">Couldn't check whether you can receive farm loads.</span>
          <WidgetRetry onRetry={() => void meQ.refetch()} busy={meQ.isFetching} />
        </div>
      )}
      {loads.map(l => (
        <LoadInboxCard key={l.load_id} l={l}
          growerName={growersQ.data?.get(l.grower_id) ?? null}
          busy={busyId === l.load_id}
          canReceive={canReceive} canReject={canReject}
          onReceive={x => void startReceive(x)}
          onReject={x => void startReject(x)} />
      ))}
    </section>
  )
}
